import { useEffect, useState } from "react";
import { toast } from "sonner";
import { billingApi, planName, type BillingStatus } from "@/lib/api";
import { CreditsPanel } from "@/components/billing/CreditsMeter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPrice(cents: number) {
  return `$${(cents / 100).toFixed(0)}`;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  active:    { label: "Active",    className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  trialing:  { label: "Free",      className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  past_due:  { label: "Past due",  className: "bg-red-500/10 text-red-400 border-red-500/20" },
  canceled:  { label: "Canceled",  className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  unpaid:    { label: "Unpaid",    className: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const PLAN_FEATURES: Record<string, string[]> = {
  starter: [
    "2,000 credits a month",
    "5 competitor domains",
    "20 watched URLs",
    "Up to 3 seats included",
    "1 workspace",
    "Hourly crawling",
    "Slack & email alerts",
    "Signal scoring",
  ],
  growth: [
    "10,000 credits a month",
    "50 competitors, shared across workspaces",
    "60 watched URLs, shared across workspaces",
    "Unlimited workspaces",
    "Teammates at $29 / seat / month",
    "Hourly crawling",
    "Slack & email alerts",
    "Daily digest",
    "Priority support",
  ],
};

// ─── Component ────────────────────────────────────────────────────────────────

export function BillingTab() {
  const { workspace } = useAuth();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  useEffect(() => {
    billingApi.get().then((res) => {
      if ("data" in res && res.data) setBilling(res.data);
      setIsLoading(false);
    });
  }, []);

  const handleCheckout = async (plan: "starter" | "growth") => {
    setLoadingAction(`checkout-${plan}`);
    const res = await billingApi.checkout(plan);
    if ("data" in res && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      // e.g. "Starter includes 1 workspace…" when a downgrade doesn't fit
      toast.error("error" in res && res.error?.message ? res.error.message : "Could not start checkout. Please try again.");
      setLoadingAction(null);
    }
  };

  const handlePortal = async () => {
    setLoadingAction("portal");
    const res = await billingApi.portal();
    if ("data" in res && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error("Could not open billing portal.");
      setLoadingAction(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel your subscription? You'll keep access until the end of the billing period.")) return;
    setLoadingAction("cancel");
    const res = await billingApi.cancel();
    if ("data" in res) {
      toast.success("Subscription will cancel at the end of the billing period.");
      // Refresh billing state
      const fresh = await billingApi.get();
      if ("data" in fresh && fresh.data) setBilling(fresh.data);
    } else {
      toast.error("Failed to cancel. Please try again.");
    }
    setLoadingAction(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // The account's plan (it covers every workspace the owner has)
  const currentPlan = billing?.plan ?? workspace?.plan ?? "trial";
  const status = billing?.subscriptionStatus ?? "trialing";
  const statusBadge = STATUS_BADGE[status] ?? STATUS_BADGE.trialing;
  const isOnPaidPlan = currentPlan === "starter" || currentPlan === "growth";
  const plans = billing?.plans ?? [];
  const isOwner = billing?.isOwner ?? true;
  const seats = billing?.seats;
  const extraSeatPrice = seats?.extraSeatPrice ?? 2900;
  const extraSeats = Math.max(0, (seats?.used ?? 1) - 1);
  const growthMonthly = 14900 + extraSeats * extraSeatPrice;

  return (
    <div className="space-y-4">

      {/* ── Current plan status ── */}
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current plan</CardTitle>
              <CardDescription className="mt-0.5">
                {isOnPaidPlan
                  ? `Renews ${formatDate(billing?.currentPeriodEnd ?? null)}`
                  : "Free plan · upgrade any time"}
              </CardDescription>
            </div>
            <Badge className={cn("border font-mono text-[11px] uppercase tracking-widest", statusBadge.className)}>
              {statusBadge.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-10 w-10 rounded-xl flex items-center justify-center",
                currentPlan === "growth" ? "bg-emerald-500/10" :
                currentPlan === "starter" ? "bg-blue-500/10" : "bg-amber-500/10"
              )}>
                <Icon
                  name={currentPlan === "growth" ? "flash" : currentPlan === "starter" ? "star" : "time-01"}
                  size={18}
                  className={
                    currentPlan === "growth" ? "text-emerald-400" :
                    currentPlan === "starter" ? "text-blue-400" : "text-amber-400"
                  }
                />
              </div>
              <div>
                <p className="font-mono text-xs font-semibold capitalize">{currentPlan}</p>
                <p className="font-sans text-xs text-muted-foreground">
                  {currentPlan === "starter" ? "$49 / month" :
                   currentPlan === "growth"
                     ? extraSeats > 0
                       ? `${formatPrice(growthMonthly)} / month — $149 + ${extraSeats} × ${formatPrice(extraSeatPrice)} seats`
                       : "$149 / month"
                     : "Free — 100 credits a month, 1 workspace, just you"}
                </p>
                {seats && (
                  <p className="font-sans text-xs text-muted-foreground">
                    {seats.used} {seats.used === 1 ? "seat" : "seats"}
                    {seats.limit ? ` of ${seats.limit}` : ""}
                    {billing?.workspaces && ` · ${billing.workspaces.used} ${billing.workspaces.used === 1 ? "workspace" : "workspaces"}${billing.workspaces.limit ? ` of ${billing.workspaces.limit}` : ""}`}
                  </p>
                )}
              </div>
            </div>

            {isOnPaidPlan && isOwner && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePortal}
                  disabled={loadingAction === "portal"}
                >
                  {loadingAction === "portal"
                    ? <Icon name="refresh" size={12} className="animate-spin mr-2" />
                    : <Icon name="invoice-01" size={12} className="mr-2" />}
                  Invoices & billing
                </Button>
              </div>
            )}
          </div>

          {!isOwner && (
            <p className="mt-4 font-sans text-xs text-muted-foreground">
              This workspace is on its owner's plan. Only the owner can change the plan or billing details.
            </p>
          )}

          {/* Past due warning */}
          {status === "past_due" && isOwner && (
            <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="font-sans text-xs text-red-400">
                Your last payment failed. Update your payment method to keep access.
              </p>
              <Button size="sm" className="mt-2" onClick={handlePortal}>
                Update payment method
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Credits ── */}
      {billing?.credits && (
        <Card>
          <CardHeader className="border-b border-border pb-3">
            <CardTitle>Credits</CardTitle>
            <CardDescription className="mt-0.5">
              {planName(billing.credits.plan)} includes {billing.credits.allowance.toLocaleString()} credits a month for AI actions, shared by all your workspaces.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <CreditsPanel credits={billing.credits} costs={billing.creditCosts} />
          </CardContent>
        </Card>
      )}

      {/* ── Pricing plans ── */}
      {isOwner && <div>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3">
          {isOnPaidPlan ? "Change plan" : "Upgrade to unlock full access"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {(["starter", "growth"] as const).map((planKey) => {
            const planData = plans.find((p) => p.key === planKey);
            const price = planData?.price ?? (planKey === "starter" ? 4900 : 14900);
            const isCurrentPlan = currentPlan === planKey;
            const isGrowth = planKey === "growth";
            const features = PLAN_FEATURES[planKey];

            return (
              <Card
                key={planKey}
                className={cn(
                  "relative transition-all",
                  isCurrentPlan && "border-primary/40 bg-primary/[0.02]",
                  isGrowth && !isCurrentPlan && "border-emerald-500/30"
                )}
              >
                {isGrowth && (
                  <div className="absolute -top-2.5 left-4">
                    <span className="bg-emerald-500 text-white font-mono text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                      Most popular
                    </span>
                  </div>
                )}
                <CardContent className="pt-5 pb-4 space-y-4">
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-widest capitalize">{planKey}</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="font-display text-3xl">{formatPrice(price)}</span>
                      <span className="font-sans text-xs text-muted-foreground">/ month</span>
                    </div>
                    {isGrowth && (
                      <p className="font-sans text-[11px] text-muted-foreground mt-1">
                        Includes you · +{formatPrice(planData?.seatPrice ?? extraSeatPrice)} / month per teammate
                        {extraSeats > 0 && !isCurrentPlan && ` (${formatPrice(growthMonthly)} / month for your ${seats?.used} seats)`}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-1.5">
                    {features.map((f) => (
                      <li key={f} className="flex items-center gap-2 font-sans text-xs text-muted-foreground">
                        <Icon name="checkmark-circle-01" size={13} className="text-primary shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full"
                    variant={isCurrentPlan ? "outline" : isGrowth ? "default" : "outline"}
                    size="sm"
                    disabled={isCurrentPlan || loadingAction === `checkout-${planKey}`}
                    onClick={() => !isCurrentPlan && handleCheckout(planKey)}
                  >
                    {loadingAction === `checkout-${planKey}` ? (
                      <Icon name="refresh" size={12} className="animate-spin mr-2" />
                    ) : null}
                    {isCurrentPlan ? "Current plan" :
                     currentPlan === "growth" && planKey === "starter" ? "Downgrade" :
                     "Upgrade"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>}

      {/* ── Cancel subscription ── */}
      {isOnPaidPlan && isOwner && (
        <>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-sans text-xs font-medium">Cancel subscription</p>
              <p className="font-sans text-xs text-muted-foreground">
                You'll keep access until {formatDate(billing?.currentPeriodEnd ?? null)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={handleCancel}
              disabled={loadingAction === "cancel"}
            >
              {loadingAction === "cancel"
                ? <Icon name="refresh" size={12} className="animate-spin mr-2" />
                : null}
              Cancel plan
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
